import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Eye, Database, Lock, Users, Globe, AlertTriangle, GraduationCap, Building2 } from 'lucide-react';

const PrivacyPage: React.FC = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8, ease: "easeOut" }
  };

  const staggerChildren = {
    animate: {
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const slideInLeft = {
    initial: { opacity: 0, x: -50 },
    animate: { opacity: 1, x: 0 },
    transition: { duration: 0.6, ease: "easeOut" }
  };

  const slideInRight = {
    initial: { opacity: 0, x: 50 },
    animate: { opacity: 1, x: 0 },
    transition: { duration: 0.6, ease: "easeOut" }
  };

  const scaleIn = {
    initial: { opacity: 0, scale: 0.8 },
    animate: { opacity: 1, scale: 1 },
    transition: { duration: 0.5, ease: "easeOut" }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-black text-white overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.1 }}
          transition={{ duration: 2 }}
          className="absolute top-20 left-10 w-64 h-64 bg-cyan-400 rounded-full blur-3xl"
        />
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.1 }}
          transition={{ duration: 2, delay: 0.5 }}
          className="absolute bottom-20 right-10 w-96 h-96 bg-purple-400 rounded-full blur-3xl"
        />
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.1 }}
          transition={{ duration: 2, delay: 1 }}
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-72 h-72 bg-pink-400 rounded-full blur-3xl"
        />
      </div>

      {/* Header */}
      <section className="bg-gradient-to-r from-indigo-900 via-purple-900 to-pink-900 py-20 shadow-2xl relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={staggerChildren}
            initial="initial"
            animate="animate"
            className="text-center"
          >
            <motion.div variants={fadeInUp}>
              <Link
                to="/signup"
                className="inline-flex items-center mb-8 text-cyan-300 hover:text-cyan-200 transition-all duration-300 hover:scale-105"
              >
                <ArrowLeft className="mr-2 h-5 w-5" />
                Back to Sign Up
              </Link>
            </motion.div>
            <motion.h1
              variants={scaleIn}
              className="text-6xl md:text-8xl font-extrabold mb-8 text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-pink-400 to-purple-400 tracking-tight"
            >
              Privacy Policy
            </motion.h1>
            <motion.p
              variants={fadeInUp}
              className="text-2xl md:text-3xl text-cyan-200 mb-4"
            >
              SEWA Education Services Data Protection Framework
            </motion.p>
            <motion.div
              variants={fadeInUp}
              className="mt-8 inline-flex items-center space-x-4 text-cyan-300 bg-cyan-400/10 px-6 py-3 rounded-full border border-cyan-400/20"
            >
              <Shield className="h-5 w-5" />
              <span className="text-sm font-medium">Last Updated: August 6, 2025</span>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10">
        <motion.div
          variants={staggerChildren}
          initial="initial"
          animate="animate"
          className="space-y-16"
        >

          {/* Introduction */}
          <motion.div
            variants={slideInLeft}
            className="backdrop-blur-xl bg-gradient-to-br from-cyan-400/10 to-purple-400/10 border border-cyan-400/20 rounded-3xl shadow-2xl p-10 hover:shadow-3xl transition-all duration-500 hover:border-cyan-400/30"
          >
            <div className="flex items-center mb-6">
              <motion.div
                initial={{ rotate: 0 }}
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                className="p-3 bg-gradient-to-r from-cyan-400 to-purple-400 rounded-full mr-4"
              >
                <GraduationCap className="h-8 w-8 text-white" />
              </motion.div>
              <h2 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-400">
                SEWA Education Services Privacy Framework
              </h2>
            </div>
            <div className="text-cyan-100 space-y-6 text-lg leading-relaxed">
              <p className="text-xl">
                At SEWA Education Services, we understand that your educational journey is deeply personal, and we are committed to protecting every piece of information you entrust to us. As India's premier educational consultancy specializing in medical admissions, engineering placements, and international study opportunities, we have developed this comprehensive privacy framework to ensure complete transparency about how we handle your academic credentials, personal details, and career aspirations. When you choose SEWA as your educational partner, you are not just sharing data with us - you are allowing us to become custodians of your dreams, and we take this responsibility with the utmost seriousness and dedication to your privacy rights.
              </p>
              <div className="bg-cyan-900/30 border border-cyan-400/30 rounded-2xl p-6 backdrop-blur-sm">
                <div className="flex items-center mb-4">
                  <AlertTriangle className="h-6 w-6 text-cyan-400 mr-3" />
                  <h3 className="font-semibold text-cyan-300 text-xl">SEWA Privacy Commitment</h3>
                </div>
                <p className="text-cyan-200">
                  By partnering with SEWA Education Services, you explicitly acknowledge and consent to our unique data handling practices specifically designed for educational consultancy, including mandatory sharing of your complete academic profile with partner universities, medical colleges, engineering institutions, and regulatory bodies across India and internationally to facilitate your admission process.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Information Collection - SEWA Specific */}
          <motion.div
            variants={slideInRight}
            className="backdrop-blur-xl bg-gradient-to-br from-yellow-400/10 to-orange-400/10 border border-yellow-400/20 rounded-3xl shadow-2xl p-10 hover:shadow-3xl transition-all duration-500 hover:border-yellow-400/30"
          >
            <div className="flex items-center mb-6">
              <motion.div
                initial={{ scale: 1 }}
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                className="p-3 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full mr-4"
              >
                <Database className="h-8 w-8 text-white" />
              </motion.div>
              <h2 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-400">
                Comprehensive Student Data Collection
              </h2>
            </div>
            <div className="text-yellow-100 space-y-6 text-lg leading-relaxed">
              <p>
                SEWA Education Services operates as a comprehensive educational ecosystem that requires extensive data collection to provide our specialized services. We collect and maintain detailed academic profiles including your complete educational history from secondary school through higher education, all standardized test scores including NEET, JEE, IELTS, TOEFL, GRE, GMAT, and other entrance examinations, certified copies of all academic transcripts and certificates, detailed family background information including parental education and occupation, comprehensive financial documentation including income certificates, bank statements, and funding sources, medical history relevant to certain professional courses, extracurricular achievements and leadership experiences, career aspirations and specialization preferences, and complete personal identification including passport details, photographs, and biometric information where required.
              </p>
              <p>
                Additionally, as part of our premium consultancy services, we collect specialized information such as research interests and publications for postgraduate applications, detailed work experience and internship records, letters of recommendation and character references, personal statements and essays for various applications, interview performance records and feedback, scholarship eligibility criteria and applications, visa documentation and immigration history, accommodation preferences and lifestyle requirements, and ongoing academic performance monitoring throughout your educational journey with SEWA.
              </p>
              <p>
                Our digital platform also automatically collects technical information including your device specifications, browser fingerprints, IP addresses and geographical locations, website interaction patterns and navigation behavior, communication logs including emails, calls, and chat transcripts, payment processing information and transaction histories, social media profiles when provided for verification purposes, and comprehensive usage analytics to improve our services and personalize your educational journey.
              </p>
            </div>
          </motion.div>

          {/* How SEWA Uses Information */}
          <motion.div
            variants={slideInLeft}
            className="backdrop-blur-xl bg-gradient-to-br from-green-400/10 to-blue-400/10 border border-green-400/20 rounded-3xl shadow-2xl p-10 hover:shadow-3xl transition-all duration-500 hover:border-green-400/30"
          >
            <div className="flex items-center mb-6">
              <motion.div
                initial={{ rotateY: 0 }}
                animate={{ rotateY: 180 }}
                transition={{ duration: 3, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" }}
                className="p-3 bg-gradient-to-r from-green-400 to-blue-400 rounded-full mr-4"
              >
                <Eye className="h-8 w-8 text-white" />
              </motion.div>
              <h2 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-blue-400">
                SEWA's Strategic Information Utilization
              </h2>
            </div>
            <div className="text-green-100 space-y-6 text-lg leading-relaxed">
              <p>
                At SEWA Education Services, your information serves as the foundation for creating personalized educational pathways that align with your career aspirations and academic potential. We utilize your comprehensive data profile to develop customized application strategies for medical colleges, engineering institutions, and international universities, ensuring optimal placement opportunities based on your qualifications, preferences, and goals. Our proprietary matching algorithms analyze your academic performance, test scores, and career interests to identify the best-fit institutions and programs, while our experienced counselors use this information to provide targeted guidance on course selection, university choices, and application timing.
              </p>
              <p>
                Your information enables us to provide specialized services including preparation of compelling personal statements and essays tailored to specific institutions, coordination of recommendation letters from appropriate academic and professional references, strategic scholarship applications based on your academic achievements and financial background, comprehensive visa application support including documentation preparation and interview coaching, and ongoing academic monitoring to ensure successful completion of your chosen programs. We also use your data for quality assurance purposes, tracking application success rates, analyzing admission trends, and continuously improving our consultancy methodologies.
              </p>
              <p>
                Beyond individual services, SEWA leverages aggregated and anonymized student data to conduct educational research, publish admission statistics and trends, develop partnerships with new educational institutions, create specialized preparation programs for entrance exams, and maintain our position as India's leading educational consultancy through data-driven insights and continuous service enhancement. Your success stories and testimonials, when provided, help us inspire and guide future students on their educational journeys.
              </p>
            </div>
          </motion.div>

          {/* SEWA Information Sharing Network */}
          <motion.div
            variants={slideInRight}
            className="backdrop-blur-xl bg-gradient-to-br from-red-400/10 to-pink-400/10 border border-red-400/20 rounded-3xl shadow-2xl p-10 hover:shadow-3xl transition-all duration-500 hover:border-red-400/30"
          >
            <div className="flex items-center mb-6">
              <motion.div
                initial={{ rotate: 0 }}
                animate={{ rotate: [0, 90, 180, 270, 360] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="p-3 bg-gradient-to-r from-red-400 to-pink-400 rounded-full mr-4"
              >
                <Building2 className="h-8 w-8 text-white" />
              </motion.div>
              <h2 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-pink-400">
                SEWA's Educational Institution Network
              </h2>
            </div>
            <div className="text-red-100 space-y-6 text-lg leading-relaxed">
              <div className="bg-red-900/30 border border-red-400/30 rounded-2xl p-8 backdrop-blur-sm">
                <div className="flex items-center mb-4">
                  <AlertTriangle className="h-8 w-8 text-red-400 mr-4" />
                  <h3 className="font-bold text-red-300 text-2xl">Mandatory Information Sharing Protocol</h3>
                </div>
                <p className="text-red-200 text-xl">
                  As SEWA Education Services operates within an extensive network of partner institutions, regulatory bodies, and service providers, your complete academic and personal information WILL be systematically shared across our trusted ecosystem to facilitate comprehensive admission processing and ensure successful educational outcomes.
                </p>
              </div>
              <p>
                SEWA maintains strategic partnerships with over 500 medical colleges, 300 engineering institutions, and 200 international universities across 25 countries, and your detailed profile will be shared with relevant institutions based on your preferences and eligibility criteria. This includes prestigious medical colleges such as AIIMS, JIPMER, and state medical universities, leading engineering institutions including IITs, NITs, and private engineering colleges, top international universities in the USA, UK, Canada, Australia, Germany, and other study destinations, specialized institutions for pharmacy, dental, veterinary, nursing, and allied health sciences, and premier business schools and management institutes for MBA and specialized programs.
              </p>
              <p>
                Beyond educational institutions, SEWA shares your information with critical service providers including government bodies such as the Medical Council of India (NMC), All India Council for Technical Education (AICTE), University Grants Commission (UGC), and state education departments, international organizations including embassy visa sections, educational credential evaluation services, and immigration consultancies, financial institutions for education loan processing, insurance providers, and scholarship organizations, documentation services including transcript authentication bodies, translation services, and courier companies, and technology partners who provide secure data processing, application management systems, and communication platforms.
              </p>
              <p>
                Our comprehensive sharing protocol ensures that your information reaches the right decision-makers at the right time, maximizing your chances of admission success while maintaining the highest standards of data security and confidentiality within our trusted network of educational and professional partners committed to your academic advancement.
              </p>
            </div>
          </motion.div>

          {/* SEWA Security Framework */}
          <motion.div
            variants={slideInLeft}
            className="backdrop-blur-xl bg-gradient-to-br from-purple-400/10 to-indigo-400/10 border border-purple-400/20 rounded-3xl shadow-2xl p-10 hover:shadow-3xl transition-all duration-500 hover:border-purple-400/30"
          >
            <div className="flex items-center mb-6">
              <motion.div
                initial={{ boxShadow: "0 0 0 0 rgba(168, 85, 247, 0.7)" }}
                animate={{ boxShadow: ["0 0 0 0 rgba(168, 85, 247, 0.7)", "0 0 0 20px rgba(168, 85, 247, 0)", "0 0 0 0 rgba(168, 85, 247, 0)"] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeOut" }}
                className="p-3 bg-gradient-to-r from-purple-400 to-indigo-400 rounded-full mr-4"
              >
                <Lock className="h-8 w-8 text-white" />
              </motion.div>
              <h2 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-indigo-400">
                SEWA Advanced Security Infrastructure
              </h2>
            </div>
            <div className="text-purple-100 space-y-6 text-lg leading-relaxed">
              <p>
                SEWA Education Services has invested heavily in creating a robust, multi-layered security infrastructure specifically designed for educational data protection, incorporating enterprise-grade encryption protocols, secure cloud storage solutions, and advanced authentication systems to safeguard your sensitive academic and personal information. Our security framework includes end-to-end SSL encryption for all data transmissions, AES-256 encryption for data storage, multi-factor authentication for staff access, regular security audits by certified cybersecurity firms, automated backup systems with disaster recovery protocols, secure API integrations with partner institutions, blockchain-based document verification systems, and comprehensive access logging and monitoring.
              </p>
              <p>
                Our dedicated IT security team continuously monitors for potential threats, implements the latest security patches and updates, conducts regular staff training on data protection protocols, and maintains compliance with international data protection standards including ISO 27001 and SOC 2 Type II. However, we acknowledge that no security system is absolutely impenetrable, and while we employ industry-leading protection measures, we cannot guarantee complete immunity from sophisticated cyber attacks, human error, or unforeseen security breaches that may potentially compromise your information despite our best efforts and strongest security measures.
              </p>
              <div className="bg-purple-900/30 border border-purple-400/30 rounded-2xl p-6 backdrop-blur-sm">
                <p className="text-xl">
                  <strong>SEWA Security Commitment:</strong> We maintain the highest available commercial security standards for educational consultancy services, but you acknowledge that absolute security cannot be guaranteed when your information is shared with multiple educational institutions, government agencies, and service providers across different countries with varying security protocols and legal frameworks.
                </p>
              </div>
            </div>
          </motion.div>

          {/* SEWA Data Retention Policy */}
          <motion.div
            variants={slideInRight}
            className="backdrop-blur-xl bg-gradient-to-br from-orange-400/10 to-yellow-400/10 border border-orange-400/20 rounded-3xl shadow-2xl p-10 hover:shadow-3xl transition-all duration-500 hover:border-orange-400/30"
          >
            <div className="flex items-center mb-6">
              <motion.div
                initial={{ opacity: 0.5 }}
                animate={{ opacity: [0.5, 1, 0.5] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                className="p-3 bg-gradient-to-r from-orange-400 to-yellow-400 rounded-full mr-4"
              >
                <Database className="h-8 w-8 text-white" />
              </motion.div>
              <h2 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-yellow-400">
                SEWA Long-Term Data Management
              </h2>
            </div>
            <div className="text-orange-100 space-y-6 text-lg leading-relaxed">
              <p>
                SEWA Education Services maintains a comprehensive long-term data retention policy designed to support your entire educational journey and beyond, recognizing that educational consultancy relationships often span multiple years and may require historical data access for various purposes including degree verification, further education applications, career advancement, and alumni services. We retain your complete academic profile and application materials for a minimum of ten years after your last interaction with our services, ensuring availability for future reference, verification requests, or additional educational pursuits that may arise throughout your career.
              </p>
              <p>
                Our retention periods vary by data type and purpose: active application materials and counseling records are maintained indefinitely while you remain an active client, completed application files and admission documentation are preserved for seven years minimum for regulatory compliance and institutional requirements, financial records and payment information are kept according to tax and accounting regulations (typically seven years), communication logs including emails, calls, and consultation notes are stored for five years to ensure continuity of service, marketing and promotional data is retained until you explicitly opt out or withdraw consent, and alumni information and success stories are maintained permanently to support our ongoing services and inspire future students.
              </p>
              <div className="bg-orange-900/30 border border-orange-400/30 rounded-2xl p-6 backdrop-blur-sm">
                <p className="text-xl">
                  <strong>Important Consideration:</strong> Even after SEWA's retention period expires, educational institutions, government agencies, scholarship organizations, and other third parties may retain your information according to their own institutional policies, regulatory requirements, and legal obligations, which may extend far beyond our control or influence.
                </p>
              </div>
            </div>
          </motion.div>

          {/* SEWA Global Data Transfers */}
          <motion.div
            variants={slideInLeft}
            className="backdrop-blur-xl bg-gradient-to-br from-pink-400/10 to-rose-400/10 border border-pink-400/20 rounded-3xl shadow-2xl p-10 hover:shadow-3xl transition-all duration-500 hover:border-pink-400/30"
          >
            <div className="flex items-center mb-6">
              <motion.div
                initial={{ rotate: 0 }}
                animate={{ rotate: 360 }}
                transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
                className="p-3 bg-gradient-to-r from-pink-400 to-rose-400 rounded-full mr-4"
              >
                <Globe className="h-8 w-8 text-white" />
              </motion.div>
              <h2 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-rose-400">
                SEWA International Data Network
              </h2>
            </div>
            <div className="text-pink-100 space-y-6 text-lg leading-relaxed">
              <p>
                As SEWA Education Services operates in the global education landscape, facilitating admissions to institutions across multiple continents, your personal and academic information will necessarily be transferred to and processed in numerous countries worldwide, each with distinct privacy laws, data protection standards, and regulatory frameworks that may provide different levels of protection compared to Indian privacy regulations. Your data will routinely cross international borders to reach universities in the United States, United Kingdom, Canada, Australia, New Zealand, Germany, Singapore, and other study destinations, processed through various cloud computing platforms, communication networks, and administrative systems located in different jurisdictions.
              </p>
              <p>
                These international transfers are essential for SEWA's core services including application submissions to foreign universities, visa processing through embassy systems, scholarship applications to international organizations, coordination with overseas education agents and representatives, communication with international student services, and integration with global education technology platforms. We work diligently to ensure appropriate safeguards are in place, but you acknowledge and accept that privacy standards, legal protections, and government access rights vary significantly between countries, and your information may be subject to foreign laws, surveillance programs, and disclosure requirements that differ substantially from Indian legal protections.
              </p>
              <div className="bg-pink-900/30 border border-pink-400/30 rounded-2xl p-6 backdrop-blur-sm">
                <p className="text-xl">
                  <strong>Global Privacy Reality:</strong> When pursuing international education through SEWA, your data will be governed by the privacy laws of destination countries, which may be more or less protective than Indian regulations, and may grant different rights regarding data access, correction, deletion, and government surveillance.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Contact SEWA Privacy Team */}
          <motion.div
            variants={fadeInUp}
            className="backdrop-blur-xl bg-gradient-to-br from-cyan-400/10 to-purple-400/10 border border-cyan-400/20 rounded-3xl shadow-2xl p-10 text-center hover:shadow-3xl transition-all duration-500 hover:border-cyan-400/30"
          >
            <div className="flex justify-center mb-6">
              <motion.div
                initial={{ scale: 1 }}
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                className="p-4 bg-gradient-to-r from-cyan-400 to-purple-400 rounded-full"
              >
                <Users className="h-12 w-12 text-white" />
              </motion.div>
            </div>
            <h2 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-400 mb-6">
              Connect with SEWA Privacy Experts
            </h2>
            <div className="text-cyan-100 space-y-6 text-lg leading-relaxed max-w-4xl mx-auto">
              <p>
                SEWA Education Services maintains a dedicated privacy and data protection team committed to addressing your concerns, answering questions about our data practices, and assisting with any privacy-related requests within the constraints of our operational requirements and legal obligations. Our privacy experts are available to discuss how your information is used, shared, and protected throughout your educational journey with SEWA, and can provide detailed explanations of our data handling processes, security measures, and compliance procedures.
              </p>
              <div className="grid md:grid-cols-2 gap-8 mt-8">
                <div className="bg-cyan-400/10 rounded-2xl p-6 border border-cyan-400/20">
                  <h3 className="text-xl font-semibold text-cyan-300 mb-4">Direct Privacy Contact</h3>
                  <p><strong>Privacy Officer:</strong> sewaprivacyteam@gmail.com</p>
                  <p><strong>Phone:</strong> +91 77086 77132</p>
                  <p><strong>WhatsApp:</strong> +91 77086 77132</p>
                </div>
                <div className="bg-purple-400/10 rounded-2xl p-6 border border-purple-400/20">
                  <h3 className="text-xl font-semibold text-purple-300 mb-4">Response Commitments</h3>
                  <p><strong>Initial Response:</strong> Within 2 business days</p>
                  <p><strong>Full Resolution:</strong> Within 30 calendar days</p>
                  <p><strong>Business Hours:</strong> Mon-Sat, 9:00 AM - 7:00 PM</p>
                  <p><strong>Emergency Contact:</strong> Available 24/7</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Final Privacy Acknowledgment */}
          <motion.div
            variants={scaleIn}
            className="backdrop-blur-xl bg-gradient-to-br from-green-400/15 to-blue-400/15 border border-green-400/30 rounded-3xl shadow-2xl p-12 text-center"
          >
            <div className="flex justify-center mb-8">
              <motion.div
                initial={{ rotate: 0, boxShadow: "0 0 0 0 rgba(34, 197, 94, 0.7)" }}
                animate={{ 
                  rotate: 360,
                  boxShadow: ["0 0 0 0 rgba(34, 197, 94, 0.7)", "0 0 0 40px rgba(34, 197, 94, 0)", "0 0 0 0 rgba(34, 197, 94, 0)"]
                }}
                transition={{ 
                  rotate: { duration: 10, repeat: Infinity, ease: "linear" },
                  boxShadow: { duration: 3, repeat: Infinity, ease: "easeOut" }
                }}
                className="p-4 bg-gradient-to-r from-green-400 to-blue-400 rounded-full"
              >
                <Shield className="h-16 w-16 text-white" />
              </motion.div>
            </div>
            <h3 className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-blue-400 mb-8">
              SEWA Privacy Agreement
            </h3>
            <div className="text-green-100 space-y-6 text-xl leading-relaxed max-w-4xl mx-auto">
              <p>
                By choosing SEWA Education Services as your educational consultancy partner, you acknowledge that you have thoroughly read, completely understood, and voluntarily agree to this comprehensive Privacy Policy, including all aspects of our data collection, processing, sharing, and retention practices specifically designed for educational consultancy services in the global education marketplace.
              </p>
              <p className="text-2xl font-semibold text-green-300">
                Your continued engagement with SEWA services, submission of application materials, and participation in our consultancy programs constitutes your ongoing, informed consent to our privacy practices and your acceptance of the inherent data sharing requirements necessary for successful educational placement and career advancement.
              </p>
            </div>
          </motion.div>

        </motion.div>
      </div>
    </div>
  );
};

export default PrivacyPage;
